import React, { useEffect } from 'react';
import { PublicLayout } from '@/components/public';
import { BRAND } from '@/config/brand';
import { SOLUTIONS_FOOTER_COLUMN } from '@/config/verticals';

import VerticalHero from './verticals/VerticalHero';
import AnswerFirstBox from './verticals/AnswerFirstBox';
import ValueBlock from './verticals/ValueBlock';
import VerticalFaq from './verticals/VerticalFaq';
import SpokeNav from './verticals/SpokeNav';
import InlineLink from './verticals/InlineLink';

const PAGE_TITLE =
  'Online Ordering for Grocers & Specialty Shops | Hungry Canadian';
const PAGE_DESCRIPTION =
  'Local e-commerce & delivery for grocers, butchers, and specialty markets — bulk CSV imports, variable-weight pricing, radius-based delivery rules, and custom-branded storefronts.';

const NAV_PROPS = {
  links: [
    { href: '#catalog', label: 'Catalog', testId: 'grocery-nav-catalog' },
    { href: '#weighted-pricing', label: 'Weighted pricing', testId: 'grocery-nav-weighted' },
    { href: '#geofencing', label: 'Delivery radius', testId: 'grocery-nav-radius' },
    { href: '#growth', label: 'Substitutions', testId: 'grocery-nav-growth' },
    { href: '#faq', label: 'FAQ', testId: 'grocery-nav-faq' },
  ],
  primaryCta: { href: '/#lead-form-anchor', label: 'Get Started', testId: 'grocery-nav-cta' },
};

const FOOTER_PROPS = {
  tagline:
    'A Canadian-owned delivery platform that keeps more of every sale with the local businesses that feed our communities.',
  columns: [
    {
      title: 'For Restaurants',
      links: [
        { href: '/', label: 'Why Hungry Canadian', testId: 'grocery-footer-why' },
        { href: '/tools', label: 'Tools', testId: 'grocery-footer-tools' },
        { href: '/features', label: 'Features', testId: 'grocery-footer-features' },
        { href: '/integrations', label: 'Integrations', testId: 'grocery-footer-integrations' },
      ],
    },
    SOLUTIONS_FOOTER_COLUMN,
    {
      title: 'Resources',
      links: [
        { href: 'https://help.hungrycanadian.com/', label: 'Help center', external: true, testId: 'grocery-footer-help' },
        { href: `mailto:${BRAND.merchantsEmail}`, label: 'Contact merchants', external: true, testId: 'grocery-footer-contact' },
      ],
    },
  ],
  legal: [
    { href: 'https://help.hungrycanadian.com/tos/', label: 'Terms of Service', external: true, testId: 'grocery-footer-tos' },
    { href: 'https://help.hungrycanadian.com/privacypolicy/', label: 'Privacy Policy', external: true, testId: 'grocery-footer-privacy' },
  ],
};

// Cross-link href constants (SSOT for this page)
const HREF_PRICING = '/#pricing';
const HREF_INTEGRATIONS_HUB = '/integrations';
// Cross-spoke sibling links
const HREF_RESTAURANTS = '/for-restaurants';
const HREF_CAFES_BAKERIES = '/for-cafes-bakeries';

// -----------------------------------------------------------------------------
// FAQ content
// -----------------------------------------------------------------------------
const GROCERY_FAQ = [
  {
    id: 'variable-weight',
    q: 'How does Hungry Canadian handle variable weight items like produce or meat?',
    a: (
      <>
        Hungry Canadian supports item weight toggles, dimension inputs, and option quantity
        multipliers (e.g., +100g increments), allowing grocers to charge accurately for items
        sold by weight.
      </>
    ),
    plainAnswer:
      'Hungry Canadian supports item weight toggles, dimension inputs, and option quantity multipliers (e.g., +100g increments), allowing grocers to charge accurately for items sold by weight.',
  },
  {
    id: 'delivery-radius-minimums',
    q: 'Can grocers set minimum order amounts based on delivery distance?',
    a: (
      <>
        Yes. Merchants can set geolocation and radius boundaries with minimum subtotal
        requirements and maximum distance limits to ensure profitable local delivery.
      </>
    ),
    plainAnswer:
      'Yes. Merchants can set geolocation and radius boundaries with minimum subtotal requirements and maximum distance limits to ensure profitable local delivery.',
  },
];

const FAQ_SCHEMA = GROCERY_FAQ.map(({ q, plainAnswer }) => ({ q, plainAnswer }));

// -----------------------------------------------------------------------------
// Value prop block content
// -----------------------------------------------------------------------------
const BLOCK_CATALOG = {
  eyebrow: 'Large-catalog operations',
  title: 'Large Catalog Management & AI Digitization',
  blurb:
    'Grocers routinely manage thousands of SKUs across dozens of categories. Hungry Canadian gives you the bulk-editing power and AI shortcuts to keep every last one accurate online.',
  bullets: [
    {
      label: 'Bulk Import & Filtering',
      detail:
        'Effortlessly upload, edit, and organize thousands of product SKUs using CSV spreadsheets. Filter catalogs instantly by category, price, or product tags within the dashboard.',
    },
    {
      label: 'AI Catalog Conversion',
      detail:
        'Convert physical price sheets, images, or PDF product lists into fully mapped online items with AI assistance.',
    },
  ],
};

const BLOCK_WEIGHTED = {
  eyebrow: 'Weight & unit accuracy',
  title: 'Variable-Weight Pricing & Multi-Unit Options',
  blurb:
    'Produce, meat, cheese, bulk goods — every specialty grocer sells by weight. Configure the exact price/gram or price/lb logic your counter staff already run manually.',
  bullets: [
    {
      label: 'Weight & Dimension Customization',
      detail:
        'Easily set product weight and dimension attributes for packaging and delivery calculations.',
    },
    {
      label: 'Unit Quantity Allowances',
      detail:
        'Configure additive pricing per unit or weight increment (e.g., adding +100g or +1lb buttons with dynamic price adjustments).',
    },
    {
      label: 'Allergen & Search Tagging',
      detail:
        'Label products with dietary badges (gluten-free, organic, halal) and search tags that index directly within the local marketplace.',
    },
  ],
};

const BLOCK_GEOFENCING = {
  eyebrow: 'Local delivery economics',
  title: 'Smart Geofencing & Radius-Based Delivery',
  blurb:
    'A 3 km delivery and a 20 km delivery cost you very different amounts to fulfill. Enforce your own minimums and caps by exact distance instead of guessing.',
  bullets: [
    {
      label: 'Distance-Based Rules',
      detail:
        'Enforce minimum order subtotals and order quantity caps based on the customer\u2019s precise distance/geolocation.',
    },
    {
      label: 'Custom Fulfillment Instructions',
      detail:
        'Provide customized pickup text, prep times, and handling notes for fragile or refrigerated goods.',
    },
  ],
};

const BLOCK_GROWTH = {
  eyebrow: 'Substitutions & upgrades',
  title: 'Real-Time Substitution & Growth Upgrades',
  blurb:
    'The single biggest online-grocery pain point is the out-of-stock item. Hungry Canadian solves it two ways: live inventory sync + a direct chat line to the customer.',
  bullets: [
    {
      label: (
        <>
          <InlineLink href={HREF_PRICING} testId="block-growth-link-pro">
            Pro Connect
          </InlineLink>
          {' & '}
          <InlineLink href={HREF_PRICING} testId="block-growth-link-vip">
            Growth VIP
          </InlineLink>{' '}
          Syncing
        </>
      ),
      detail: (
        <>
          Connect directly with your{' '}
          <InlineLink href={HREF_INTEGRATIONS_HUB} testId="block-growth-link-pos">
            retail POS
          </InlineLink>{' '}
          to sync live inventory and avoid overselling.
        </>
      ),
    },
    {
      label: 'Instant Order Messaging',
      detail:
        'Connect instantly with shoppers or drivers via active order chat to handle item substitutions.',
    },
    {
      label: 'Custom Storefront Design',
      detail:
        'Upgrade to a custom-designed digital storefront tailored specifically to your brand identity.',
    },
  ],
};

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------
export default function ForGrocerySpecialtyPage() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = PAGE_TITLE;

    let meta = document.querySelector('meta[name="description"]');
    const prevDescription = meta?.getAttribute('content');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', PAGE_DESCRIPTION);

    return () => {
      document.title = prevTitle;
      if (prevDescription != null) meta.setAttribute('content', prevDescription);
    };
  }, []);

  return (
    <PublicLayout navProps={NAV_PROPS} footerProps={FOOTER_PROPS}>
      <div data-testid="for-grocery-specialty-page">
        <VerticalHero
          eyebrow="For Grocery & Specialty"
          h1="Local E-Commerce & Delivery for Grocers & Specialty Shops"
          tagline={
            <>
              Sell specialty foods, weighted produce, and high-SKU inventories online with
              automated catalog tools, custom radius rules, and dedicated storefront designs.
            </>
          }
          primaryCta={{
            href: '/#lead-form-anchor',
            label: 'Get Started',
            testId: 'grocery-hero-primary-cta',
          }}
          secondaryCta={{
            href: HREF_PRICING,
            label: 'See Pricing',
            testId: 'grocery-hero-secondary-cta',
          }}
        />

        <AnswerFirstBox
          title="How Hungry Canadian Powers Grocery & Specialty Markets"
          testId="grocery-answer-first"
        >
          Hungry Canadian enables local grocers,{' '}
          <InlineLink href={HREF_RESTAURANTS} testId="answer-link-butchers">
            butchers
          </InlineLink>
          , and{' '}
          <InlineLink href={HREF_CAFES_BAKERIES} testId="answer-link-specialty">
            specialty shops
          </InlineLink>{' '}
          to upload and manage thousands of SKUs via bulk CSV or AI conversion. The platform
          supports variable-weight pricing, custom delivery radii with distance-based subtotals,
          real-time driver/customer chat, and custom branded storefronts.
        </AnswerFirstBox>

        <div id="catalog">
          <ValueBlock {...BLOCK_CATALOG} testId="grocery-block-catalog" />
        </div>
        <div id="weighted-pricing">
          <ValueBlock {...BLOCK_WEIGHTED} testId="grocery-block-weighted" reverse />
        </div>
        <div id="geofencing">
          <ValueBlock {...BLOCK_GEOFENCING} testId="grocery-block-geofencing" />
        </div>
        <div id="growth">
          <ValueBlock {...BLOCK_GROWTH} testId="grocery-block-growth" reverse />
        </div>

        <VerticalFaq
          items={GROCERY_FAQ}
          schema={FAQ_SCHEMA}
          testId="grocery-faq"
          eyebrow="Grocery & Specialty FAQ"
          description="Answers to the catalog-scale, weighted-pricing, and delivery-radius questions grocers ask most before switching platforms."
        />

        <SpokeNav currentSlug="for-grocery-specialty" />
      </div>
    </PublicLayout>
  );
}
